<template>
  <div>
    <Card title="Artist Application Detail">
      <template v-slot:button>
        <Button @clicked="goBack" variant="default" label="Back" />
        <Button v-if="canReview" @clicked="approve" variant="primary" label="Approve" />
        <Button v-if="canReview" @clicked="openReject" variant="primary" label="Reject" />
      </template>
      <template v-slot:body>
        <div class="w-full">
          <div class="flex flex-col py-6" style="min-width: 600px">
            <div class="flex flex-col mb-6">
              <span :class="statusBadgeClass(application.status)">{{ statusLabel(application.status) }}</span>
            </div>
            <div class="flex flex-wrap">
              <div class="w-1/2 pr-4">
                <form-item label="First Name">
                  <template v-slot>
                    <p class="text-left font-bold">{{ application.first_name }}</p>
                  </template>
                </form-item>
                <form-item label="Last Name">
                  <template v-slot>
                    <p class="text-left font-bold">{{ application.last_name }}</p>
                  </template>
                </form-item>
                <form-item label="Email">
                  <template v-slot>
                    <p class="text-left font-bold">{{ application.email }}</p>
                  </template>
                </form-item>
                <form-item label="Phone Number">
                  <template v-slot>
                    <p class="text-left font-bold">{{ application.phone_number }}</p>
                  </template>
                </form-item>
                <form-item label="Monetize Choice">
                  <template v-slot>
                    <p class="text-left font-bold">{{ monetizeLabel }}</p>
                  </template>
                </form-item>
                <form-item label="Submitted">
                  <template v-slot>
                    <p class="text-left font-bold">{{ application.submitted_at }}</p>
                  </template>
                </form-item>
              </div>
              <div class="w-1/2 pr-4">
                <form-item label="Adventist Union / Conference / Mission">
                  <template v-slot>
                    <p class="text-left font-bold">{{ application.missions }}</p>
                  </template>
                </form-item>
                <form-item label="Church Name">
                  <template v-slot>
                    <p class="text-left font-bold">{{ application.church }}</p>
                  </template>
                </form-item>
                <form-item label="Demo Links">
                  <template v-slot>
                    <p class="text-left font-bold whitespace-pre-line">{{ application.links }}</p>
                  </template>
                </form-item>
                <form-item label="Self Introduction">
                  <template v-slot>
                    <p class="text-left font-bold whitespace-pre-line">{{ application.self_introduction }}</p>
                  </template>
                </form-item>
                <form-item label="Legal Accepted At">
                  <template v-slot>
                    <p class="text-left font-bold">{{ application.legal_accepted_at }}</p>
                  </template>
                </form-item>
              </div>
            </div>
            <div v-if="application.status === 'REJECTED' && application.rejection_reason" class="w-full mt-4 p-4 bg-red-50 border border-red-200 rounded">
              <form-item label="Rejection Reason">
                <template v-slot>
                  <p class="text-left font-bold text-red-700">{{ application.rejection_reason }}</p>
                </template>
              </form-item>
            </div>
            <div class="w-full mt-6">
              <form-item label="Uploaded Files">
                <template v-slot>
                  <div v-if="files.length === 0" class="text-left text-gray-500">No files attached.</div>
                  <ul v-else class="text-left">
                    <li v-for="file in files" :key="file.id" class="flex items-center justify-between py-2 border-b border-gray-100">
                      <div>
                        <span class="inline-block px-2 py-1 mr-2 text-xs font-bold rounded bg-gray-200">{{ file.type.toUpperCase() }}</span>
                        <span class="font-bold">{{ file.original_name }}</span>
                        <span class="text-gray-500 ml-2 text-xs">{{ humanFileSize(file.size) }}</span>
                      </div>
                      <Button @clicked="downloadFile(file)" variant="default" label="Download" />
                    </li>
                  </ul>
                </template>
              </form-item>
            </div>
          </div>
        </div>
      </template>
    </Card>
    <ModalDialog
      ref="rejectionModal"
      :isShowingModal="isShowingModalReject"
      :isLoading="isLoading"
      :error="isError"
      labelSubmit="Submit Rejection"
      title="Reject Application"
      @toggleModal="isShowingModalReject = !isShowingModalReject"
      @save="submitRejected()"
    >
      <template slot="body">
        <RejectionForm ref="rejectionForm" :toggle-reset="isShowingModalReject" :song-rejection="rejectionTarget" :custom-submit="customReject" @on-submit-rejection="onSubmitRejectionForm">
        </RejectionForm>
      </template>
    </ModalDialog>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import axios from 'axios';
import fileDownload from 'js-file-download';
import { authHeader2 } from '@/helper/authHeader';
import Card from '@/components/Layout/Card.vue';
import { Button, FormItem } from '@/components/Layout/Form';
import ModalDialog from '../../components/Common/ModalDialog';
import RejectionForm from '../contents/RejectionForm';

const API_URL = process.env.API_URL;

export default {
  name: 'ArtistApplicationDetailPage',
  data() {
    return {
      isError: false,
      isShowingModalReject: false,
      applicationId: Number(this.$route.params.id),
      rejectionTarget: { id: '' },
    };
  },
  async mounted() {
    this.applicationId = Number(this.$route.params.id);
    await this.requestDetailArtistApplication(this.applicationId);
  },
  computed: {
    ...mapGetters('NotificationModule', ['isLoading']),
    ...mapGetters('AuthModule', ['isSuperAdmin', 'isAdmin', 'isOps', 'isRecruitment']),
    ...mapGetters('ArtistModule', ['artistApplication']),
    application() {
      return this.artistApplication || {};
    },
    files() {
      return this.application.files || [];
    },
    monetizeLabel() {
      return this.application.monetize_choice === 'monetize' ? 'Monetize' : 'Free';
    },
    canReview() {
      return this.application.status === 'IN_REVIEW' && (this.isOps || this.isAdmin || this.isSuperAdmin || this.isRecruitment);
    },
  },
  methods: {
    ...mapActions('ArtistModule', ['requestDetailArtistApplication', 'requestApproveArtistApplication', 'requestRejectArtistApplication']),
    goBack() {
      this.$router.push('/artists/onboarding');
    },
    statusLabel(status) {
      switch (status) {
        case 'APPROVED':
          return 'Approved';
        case 'REJECTED':
          return 'Rejected';
        case 'IN_REVIEW':
        default:
          return 'In Review';
      }
    },
    statusBadgeClass(status) {
      const base = 'inline-block px-3 py-1 rounded-full text-xs font-bold ';
      switch (status) {
        case 'APPROVED':
          return `${base}bg-green-100 text-green-700`;
        case 'REJECTED':
          return `${base}bg-red-100 text-red-700`;
        case 'IN_REVIEW':
        default:
          return `${base}bg-yellow-100 text-yellow-700`;
      }
    },
    humanFileSize(bytes) {
      if (!bytes) return '';
      const units = ['B', 'KB', 'MB', 'GB'];
      let i = 0;
      let size = bytes;
      while (size >= 1024 && i < units.length - 1) {
        size /= 1024;
        i += 1;
      }
      return `${size.toFixed(1)} ${units[i]}`;
    },
    async approve() {
      Vue.swal({
        title: 'Approve Application',
        text: `Are you sure to approve artist application: ${this.application.first_name} ${this.application.last_name}`,
        icon: 'warning',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        showCancelButton: true,
      }).then(async (result) => {
        if (result.value) {
          const data = { id: this.applicationId };
          await this.requestApproveArtistApplication({ data, filter: null });
          this.$swal('Approved', 'You successfully approve artist application', 'success');
          await this.requestDetailArtistApplication(this.applicationId);
        }
      });
    },
    openReject() {
      this.rejectionTarget = { id: this.applicationId };
      this.isShowingModalReject = true;
    },
    async submitRejected() {
      await this.$refs.rejectionForm.submitRejection();
    },
    async customReject({ id, note }) {
      const data = { id, rejection_reason: note };
      await this.requestRejectArtistApplication({ data, filter: null });
    },
    async onSubmitRejectionForm() {
      this.isShowingModalReject = false;
      this.rejectionTarget = { id: '' };
      await this.requestDetailArtistApplication(this.applicationId);
    },
    async downloadFile(file) {
      try {
        const res = await axios({
          url: `${API_URL}/admin/artist-application/download-file/${this.applicationId}/${file.id}`,
          method: 'get',
          headers: authHeader2(),
          responseType: 'blob',
        });
        fileDownload(res.data, file.original_name);
      } catch (err) {
        this.$swal('Error', 'File not found', 'error');
        throw err.response;
      }
    },
  },
  components: { Card, Button, FormItem, ModalDialog, RejectionForm },
};
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/views/ArtistContent/ArtistApplicationDetail.vue